import { ControlType, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, HasVariablesInGit, Permission, VariableType } from "@octopusdeploy/octopus-server-client";
import type { LibraryVariableSetResource, ProjectResource, TenantResource, TenantVariableResource, ScopeValues, VariableSetResource, ResourcesById, EnvironmentV2Resource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { flatten } from "lodash";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { PageHeaderBranchSelector } from "~/areas/projects/components/BranchSelector/PageHeaderBranchSelector";
import { useProjectContext } from "~/areas/projects/context";
import mergeScopeValues from "~/areas/variables/MergeScopeValues";
import FilterableVariableDisplayer from "~/areas/variables/VariableDisplayer/FilterableVariableDisplayer";
import type { ValueWithSource } from "~/areas/variables/VariableDisplayer/VariableDisplayer";
import { convertVariableResourcesToVariablesWithSource } from "~/areas/variables/convertVariableResourcesToVariablesWithSource";
import { repository } from "~/clientInstance";
import type { DoBusyTask, Errors } from "~/components/DataBaseComponent";
import DataBaseComponent, { useDoBusyTaskEffect } from "~/components/DataBaseComponent";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import convertPropertyValueResourceToString from "~/components/convertPropertyValueResourceToString";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import type { ValueSource } from "../../../../variables/SourceLink/SourceLink";
import type { VariableWithSource } from "../../../../variables/VariableDisplayer";
import { default as groupVariablesByName } from "../../../../variables/groupVariablesByName";
import { useEphemeralEnvironmentsEnabled } from "../../EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { VcsError } from "../../VersionControl/VcsError";
interface LibraryVariableSetWithVariables {
    variableSet: VariableSetResource;
    libraryVariableSet: LibraryVariableSetResource;
}
type AllVariablesPageProps = {
    doBusyTask: DoBusyTask;
    errors?: Errors;
    busy?: Promise<void>;
};
const pageTitle = "All Variables";
const AllVariablesPage: React.FC<AllVariablesPageProps> = (props: AllVariablesPageProps) => {
    const allVariables = useAllVariablesDataLoading(props.doBusyTask);
    const projectContext = useProjectContext();
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const { model: project, gitRef, gitRefValidationError } = projectContext.state;
    const spaceNavigation = useSpaceAwareNavigation();
    const { search: urlSearch } = useLocation();
    const getVariables = (): ReadonlyArray<VariableWithSource> => {
        if (!allVariables.projectVariableSet) {
            return [];
        }
        return [
            ...buildProjectVariables(project, allVariables.projectVariableSet),
            ...buildLibraryVariableSetVariables(allVariables.libraryVariableSets),
            ...buildTenantLibraryVariables(project, allVariables.tenants, allVariables.tenantVariables),
            ...buildTenantProjectVariables(project, allVariables.tenants, allVariables.tenantVariables),
        ];
    };
    const getAvailableScopes = (): ScopeValues => {
        const allScopeValues: ScopeValues[] = allVariables.projectVariableSet ? [allVariables.projectVariableSet.ScopeValues, ...allVariables.libraryVariableSets.map((set) => set.variableSet.ScopeValues)] : [];
        return mergeScopeValues(allScopeValues);
    };
    const navigateToGitRef = (gitRef: string) => {
        // Ideally, instead of concatenating the URL search property, it should use links.branchAllVariablesPage.generateUrl(routeParams, queryParams) to get the Url, and take queryParams from routing infrastructure.
        // We need to refactor the Advanced Filters in this page to achieve the ideal pattern.
        const newUrl = links.branchAllVariablesPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, branchName: gitRef }).resolveWithSpaceId(project.SpaceId);
        const fullUrl = `${newUrl}${urlSearch}`;
        return spaceNavigation.navigate(fullUrl);
    };
    if (gitRefValidationError && gitRefValidationError.Type !== "Success") {
        return (<PageContent header={{ title: pageTitle }}>
                <VcsError project={project} error={gitRefValidationError} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/>
            </PageContent>);
    }
    return (<PageContent busy={props.busy} errors={props.errors} header={{ title: pageTitle, contextSelector: <PageHeaderBranchSelector project={project} gitRef={projectContext.state.gitRef?.CanonicalName} setGitRef={navigateToGitRef}/> }}>
            <FilterableVariableDisplayer availableScopes={getAvailableScopes()} variableSections={[getVariables()]} doBusyTask={props.doBusyTask} ephemeralEnvironmentsAreEnabled={ephemeralEnvironmentsAreEnabled} environments={allVariables.environments}/>
        </PageContent>);
};
AllVariablesPage.displayName = "AllVariablesPage"
function getVariableTypeFromDisplaySettings(type?: ControlType): VariableType {
    switch (type) {
        case ControlType.Sensitive:
            return VariableType.Sensitive;
        case ControlType.Certificate:
            return VariableType.Certificate;
        case ControlType.AmazonWebServicesAccount:
            return VariableType.AmazonWebServicesAccount;
        case ControlType.AzureAccount:
            return VariableType.AzureAccount;
        case ControlType.GoogleCloudAccount:
            return VariableType.GoogleCloudAccount;
        case ControlType.GenericOidcAccount:
            return VariableType.GenericOidcAccount;
        case ControlType.WorkerPool:
            return VariableType.WorkerPool;
        case ControlType.UsernamePasswordAccount:
            return VariableType.UsernamePasswordAccount;
        default:
            return VariableType.String;
    }
}
interface AllVariablesForProject {
    projectVariableSet: VariableSetResource | undefined;
    libraryVariableSets: LibraryVariableSetWithVariables[];
    tenants: TenantResource[];
    tenantVariables: TenantVariableResource[];
    environments: EnvironmentV2Resource[];
}
const initialEmptyProjectVariableState: AllVariablesForProject = {
    tenants: [],
    libraryVariableSets: [],
    tenantVariables: [],
    projectVariableSet: undefined,
    environments: [],
};
function useAllVariablesDataLoading(doBusyTask: DoBusyTask): AllVariablesForProject {
    const projectContext = useProjectContext();
    const project = projectContext.state.model;
    const loadProjectVariableSet = useProjectVariableSetLoader();
    const [state, setState] = React.useState<AllVariablesForProject>(initialEmptyProjectVariableState);
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    useDoBusyTaskEffect(doBusyTask, async () => {
        const libraryVariableSetVariables = await loadLibraryVariableSetVariables(project.IncludedLibraryVariableSetIds);
        const projectVariableSet = await loadProjectVariableSet();
        const projectId = project.Id;
        const tenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? await repository.Tenants.all({ projectId }) : [];
        const tenantVariables = await repository.TenantVariables.getOnlyProjectScoped(projectId);
        const environments = await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repository);
        setState({
            projectVariableSet: projectVariableSet,
            tenants: tenants,
            tenantVariables: tenantVariables,
            libraryVariableSets: libraryVariableSetVariables,
            environments: environments,
        });
    }, [project, projectContext.state.gitRef, ephemeralEnvironmentsAreEnabled], { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    return state;
}
function useProjectVariableSetLoader() {
    const projectContext = useProjectContext();
    const project = projectContext.state.model;
    return React.useCallback(async () => {
        if (HasVariablesInGit(project.PersistenceSettings) && !projectContext.state.gitRef) {
            // If we've just changed from an invalid branch, GitRef might be null. Wait until it's set before
            // we proceed with the reload or the call on the next line will fail.
            return;
        }
        const variableSet = await projectContext.state.projectContextRepository.Variables.get();
        if (HasVariablesInGit(project.PersistenceSettings)) {
            const sensitiveVariableSet = await projectContext.state.projectContextRepository.Variables.getSensitive();
            // We are making two separate variable set requests for Git projects (text and secret variables use a
            // separate endpoint), but we need to return a single VariableSetResource. We're re-building a variable
            // set using all of the values from variableSet but combining the variables from both the text and secret
            // sets
            return {
                ...variableSet,
                Variables: [...variableSet.Variables, ...sensitiveVariableSet.Variables],
            };
        }
        else {
            // If the project doesn't have Git variables, everything will be in variableSet so we just return that
            // as is.
            return variableSet;
        }
    }, [projectContext.state.projectContextRepository.Variables, project.PersistenceSettings, projectContext.state.gitRef]);
}
export interface LoadedLibraryVariableSets {
    variableSet: VariableSetResource;
    libraryVariableSet: LibraryVariableSetResource;
}
export async function loadLibraryVariableSetVariables(includedLibraryVariableSetIds: string[]): Promise<LoadedLibraryVariableSets[]> {
    const libraryVariableSets = await repository.LibraryVariableSets.all({ ids: includedLibraryVariableSetIds });
    const allVariableSets = await repository.Variables.all({ ids: libraryVariableSets.map((l) => l.VariableSetId) });
    const allVariableSetsMap = allVariableSets.reduce((acc: ResourcesById<VariableSetResource>, resource) => {
        acc[resource.Id] = resource;
        return acc;
    }, {});
    return libraryVariableSets.map((lvs) => ({ variableSet: allVariableSetsMap[lvs.VariableSetId], libraryVariableSet: lvs }));
}
const buildTenantProjectVariables = (project: ProjectResource, tenants: TenantResource[], tenantVariables: TenantVariableResource[]): ReadonlyArray<VariableWithSource> => {
    const projectId = project.Id;
    const namedValues = flatten(tenantVariables.map(getAllProjectVariablesForTenant));
    const groupedByNameValues = groupVariablesByName(namedValues, (namedValue) => namedValue.name);
    return Object.keys(groupedByNameValues).map((name) => ({ name, values: groupedByNameValues[name].map((nv) => nv.value) }));
    function getAllProjectVariablesForTenant(tenantVariables: TenantVariableResource): Array<{
        name: string;
        value: ValueWithSource;
    }> {
        const tenant = tenants.find((t) => t.Id === tenantVariables.TenantId);
        const projectVariables = tenantVariables.ProjectVariables[projectId];
        const source: ValueSource = {
            spaceId: tenantVariables.SpaceId,
            tenantId: tenant?.Id ?? tenantVariables.TenantId,
            tenantName: tenant?.Name ?? "Unknown tenant",
            isDisabled: !!tenant?.IsDisabled,
            type: "project",
        };
        if (isFeatureToggleEnabled("CommonVariableScopingFeatureToggle")) {
            return projectVariables.Templates.map((template) => {
                const valuesForTemplate = projectVariables.TenantVariables[template.Id];
                if (valuesForTemplate) {
                    return valuesForTemplate.map((value) => ({
                        name: template.Name,
                        value: {
                            type: getVariableTypeFromDisplaySettings(template.DisplaySettings["Octopus.ControlType"]),
                            scope: {
                                Environment: value.Scope.EnvironmentIds,
                            },
                            value: convertPropertyValueResourceToString(value.Value || template.DefaultValue),
                            source,
                            isPrompted: false,
                        },
                    }));
                }
                else {
                    return {
                        name: template.Name,
                        value: {
                            type: getVariableTypeFromDisplaySettings(template.DisplaySettings["Octopus.ControlType"]),
                            scope: {},
                            value: convertPropertyValueResourceToString(template.DefaultValue),
                            source,
                            isPrompted: false,
                        },
                    };
                }
            }).flat(2);
        }
        else {
            return flatten(projectVariables.Templates.map((template) => Object.keys(projectVariables.Variables).map((environmentId) => {
                const environmentValues = projectVariables.Variables[environmentId];
                return {
                    name: template.Name,
                    value: {
                        type: getVariableTypeFromDisplaySettings(template.DisplaySettings["Octopus.ControlType"]),
                        scope: {
                            Environment: [environmentId],
                        },
                        value: convertPropertyValueResourceToString(environmentValues[template.Id] || template.DefaultValue),
                        source,
                        isPrompted: false,
                    },
                };
            })));
        }
    }
};
const buildLibraryVariableSetVariables = (libraryVariableSets: LibraryVariableSetWithVariables[]): ReadonlyArray<VariableWithSource> => flatten(libraryVariableSets.map((set) => {
    const source = {
        spaceId: set.libraryVariableSet.SpaceId,
        variableSetName: set.libraryVariableSet.Name,
        variableSetId: set.libraryVariableSet.Id,
    };
    return convertVariableResourcesToVariablesWithSource(set.variableSet.Variables, source);
}));
const buildTenantLibraryVariables = (project: ProjectResource, tenants: TenantResource[], tenantVariables: TenantVariableResource[]): ReadonlyArray<VariableWithSource> => {
    const libraryVariableSetIds = project.IncludedLibraryVariableSetIds;
    const namedValues = flatten(tenantVariables.map(getAllLibrarySetVariablesForTenant));
    const groupedByNameValues = groupVariablesByName(namedValues, (namedValue) => namedValue.name);
    return Object.keys(groupedByNameValues).map((name) => ({ name, values: groupedByNameValues[name].map((nv) => nv.value) }));
    function getAllLibrarySetVariablesForTenant(tenantVariables: TenantVariableResource): Array<{
        name: string;
        value: ValueWithSource;
    }> {
        const tenant = tenants.find((t) => t.Id === tenantVariables.TenantId);
        const libraryVariablesLookup = tenantVariables.LibraryVariables;
        const source: ValueSource = {
            spaceId: tenantVariables.SpaceId,
            tenantId: tenant?.Id ?? tenantVariables.TenantId,
            tenantName: tenant?.Name ?? "Unknown tenant",
            isDisabled: !!tenant?.IsDisabled,
            type: "library",
        };
        if (isFeatureToggleEnabled("CommonVariableScopingFeatureToggle")) {
            return libraryVariableSetIds
                .map((variableSetId) => {
                const projectLibraryVariables = libraryVariablesLookup[variableSetId];
                const templates = projectLibraryVariables.Templates;
                const valuesForAllTemplates = projectLibraryVariables.TenantVariables;
                return templates.map((template) => {
                    const valuesForTemplate = valuesForAllTemplates[template.Id];
                    if (valuesForTemplate) {
                        return valuesForTemplate.map((value) => ({
                            name: template.Name,
                            value: {
                                type: getVariableTypeFromDisplaySettings(template.DisplaySettings["Octopus.ControlType"]),
                                scope: {
                                    Environment: value.Scope.EnvironmentIds,
                                },
                                value: convertPropertyValueResourceToString(value.Value || template.DefaultValue),
                                source,
                                isPrompted: false,
                            },
                        }));
                    }
                    else {
                        return {
                            name: template.Name,
                            value: {
                                type: getVariableTypeFromDisplaySettings(template.DisplaySettings["Octopus.ControlType"]),
                                scope: {},
                                value: convertPropertyValueResourceToString(template.DefaultValue),
                                source,
                                isPrompted: false,
                            },
                        };
                    }
                });
            })
                .flat(2);
        }
        else {
            return flatten(libraryVariableSetIds.map((variableSetId) => {
                const projectLibraryVariables = libraryVariablesLookup[variableSetId];
                const templates = projectLibraryVariables.Templates;
                const values = projectLibraryVariables.Variables;
                return templates.map((varTemplate) => ({
                    name: varTemplate.Name,
                    value: {
                        type: getVariableTypeFromDisplaySettings(varTemplate.DisplaySettings["Octopus.ControlType"]),
                        scope: {},
                        value: convertPropertyValueResourceToString(values[varTemplate.Id] || varTemplate.DefaultValue),
                        source,
                        isPrompted: false,
                    },
                }));
            }));
        }
    }
};
function buildProjectVariables(project: ProjectResource, variableSet: VariableSetResource): ReadonlyArray<VariableWithSource> {
    const source = {
        projectName: project.Name,
        projectId: project.Id,
        spaceId: project.SpaceId,
    };
    return convertVariableResourcesToVariablesWithSource(variableSet.Variables, source);
}
class SharedAllVariablesPage extends DataBaseComponent<{}> {
    render() {
        return <AllVariablesPage doBusyTask={this.doBusyTask} errors={this.errors} busy={this.state?.busy}/>;
    }
    static displayName = "SharedAllVariablesPage";
}
export default SharedAllVariablesPage;
